import { memo } from "react"
import { FiEye, FiTrash2 } from "react-icons/fi"

import type { StudentResponse } from "@/types/student"
import {
  Button,
  Flex,
  Heading,
  IconButton,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface StudentCardProps {
  student: StudentResponse
  onEdit?: (student: StudentResponse) => void
  onDelete?: (student: StudentResponse) => void
  onView?: (student: StudentResponse) => void
}

const StudentCard = memo(
  ({ student, onEdit, onDelete, onView }: StudentCardProps) => {
    const titleValue = student.first_name ?? student.id

    const subtitleValue = student.last_name ?? undefined

    return (
      <Stack
        borderWidth="1px"
        borderRadius="lg"
        padding={4}
        gap={4}
        bg="bg.surface"
        shadow="sm"
      >
        <Stack gap={1}>
          <Heading size="sm">{titleValue}</Heading>
          {subtitleValue !== undefined && (
            <Text color="fg.muted" fontSize="sm">
              {subtitleValue || "No description"}
            </Text>
          )}
        </Stack>

        <Stack gap={1} fontSize="xs" color="fg.subtle">
          <Text>Created: {new Date(student.created_at).toLocaleString()}</Text>
          <Text>Updated: {new Date(student.updated_at).toLocaleString()}</Text>
        </Stack>

        <Separator />
        <Stack gap={2} fontSize="sm">
          <Flex
            key="first_name"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">First Name</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(student.first_name, "str")}
            </Text>
          </Flex>
          <Flex
            key="last_name"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Last Name</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(student.last_name, "str")}
            </Text>
          </Flex>
          <Flex
            key="date_of_birth"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Date Of Birth</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(student.date_of_birth, "date")}
            </Text>
          </Flex>
          <Flex key="gender" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Gender</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(student.gender, "str")}
            </Text>
          </Flex>
          <Flex
            key="household_id"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Household Id</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(student.household_id, "str")}
            </Text>
          </Flex>
          <Flex
            key="current_swim_level"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Current Swim Level</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(student.current_swim_level, "str")}
            </Text>
          </Flex>
          <Flex
            key="skill_level_notes"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Skill Level Notes</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(student.skill_level_notes, "str")}
            </Text>
          </Flex>
          <Flex
            key="has_asthma"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Has Asthma</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(student.has_asthma, "bool")}
            </Text>
          </Flex>
          <Flex
            key="preferred_location"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Preferred Location</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(student.preferred_location, "str")}
            </Text>
          </Flex>
          <Flex key="notes" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Notes</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(student.notes, "str")}
            </Text>
          </Flex>
          <Flex
            key="is_active"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Is Active</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(student.is_active, "bool")}
            </Text>
          </Flex>
          <Flex
            key="photo_url"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Photo Url</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(student.photo_url, "str")}
            </Text>
          </Flex>
        </Stack>

        <Flex gap={2} justify="flex-end">
          {onView && (
            <Button size="sm" variant="ghost" onClick={() => onView(student)}>
              <FiEye /> View
            </Button>
          )}
          {onEdit && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              onClick={() => onEdit(student)}
            >
              Edit
            </Button>
          )}
          {onDelete && (
            <IconButton
              size="sm"
              variant="ghost"
              colorScheme="red"
              aria-label="Delete student"
              onClick={() => onDelete(student)}
            >
              <FiTrash2 />
            </IconButton>
          )}
        </Flex>
      </Stack>
    )
  },
)

StudentCard.displayName = "StudentCard"

export default StudentCard
